import type { Tone } from "../../shared/ui";
import type { TemplateVersionStatus } from "../../shared/types/api";

/**
 * How template vocabulary reads.
 *
 * <p>The status labels say what the state means for the reader rather than naming it: "In use"
 * tells an administrator that changing this would affect the next release, which "ACTIVE" does
 * not.
 */
export const VERSION_STATUS_LABELS: Record<TemplateVersionStatus, string> = {
  DRAFT: "Draft",
  ACTIVE: "In use",
  RETIRED: "Superseded",
};

/**
 * Violet for in-use, not green.
 *
 * <p>Green would read as "this is fine", and the point of the badge is the opposite: an in-use
 * version is the one that cannot be edited, and the one every new release will be built from.
 */
export const VERSION_STATUS_TONE: Record<TemplateVersionStatus, Tone> = {
  DRAFT: "amber",
  ACTIVE: "violet",
  RETIRED: "gray",
};

/**
 * What a version is worth, in one line.
 *
 * <p>Counts rather than a completeness score. An administrator deciding whether to adopt a
 * template wants to know what it will impose, and "7 prerequisites, 4 approvals" says that where
 * a percentage says nothing.
 */
export function describeVersionContents(counts: {
  prerequisites: number;
  approvals: number;
  executionSteps: number;
  validationChecks: number;
}): string {
  const parts: string[] = [];
  if (counts.prerequisites > 0) {
    parts.push(`${counts.prerequisites} prerequisite${counts.prerequisites === 1 ? "" : "s"}`);
  }
  if (counts.approvals > 0) {
    parts.push(`${counts.approvals} approval${counts.approvals === 1 ? "" : "s"}`);
  }
  if (counts.executionSteps > 0) {
    parts.push(`${counts.executionSteps} step${counts.executionSteps === 1 ? "" : "s"}`);
  }
  if (counts.validationChecks > 0) {
    parts.push(`${counts.validationChecks} check${counts.validationChecks === 1 ? "" : "s"}`);
  }
  return parts.length === 0 ? "Nothing defined yet" : parts.join(" · ");
}

/**
 * What retiring or superseding a version would affect.
 *
 * <p>Shown before anybody activates a replacement. The answer is always "none" in the sense that
 * matters — §52 means existing releases are untouched — and saying so explicitly is what stops
 * an administrator hesitating over a safe action.
 */
export function describeImpact(releasesBuiltFrom: number): string {
  if (releasesBuiltFrom === 0) {
    return "No releases were built from this version yet.";
  }
  const releases = `${releasesBuiltFrom} release${releasesBuiltFrom === 1 ? "" : "s"}`;
  return `${releases} ${releasesBuiltFrom === 1 ? "was" : "were"} built from this version. `
    + `${releasesBuiltFrom === 1 ? "It keeps" : "They keep"} the rules copied at the time — `
    + "publishing a new version changes nothing for them.";
}
